import { type ButtonHTMLAttributes, type ReactNode, createContext, forwardRef, useContext, useEffect, useState } from 'react';
import { useBlocker } from 'react-router';
import { Check, CircleAlert, Copy, Info, RefreshCw, TriangleAlert } from 'lucide-react';
import { Button, ConfirmDialog, Field, ImageUpload, Input, cx, useToast } from '@lp/portal-ui';
import { LANG_META, type Lang, type MediaRef } from '@lp/shared';
import { api } from '../api.ts';
import { errorMessage } from '../lib/errors.ts';
import { fromLocalInput, toLocalInput } from '../lib/format.ts';
import { SessionContext } from '../session.tsx';
import { AiImageButton, type AiImageSeed } from './AiImage.tsx';

/** Small round icon button for table row actions. */
export const IconButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { label: string; tone?: 'default' | 'danger' }>(
  function IconButton({ label, tone = 'default', className, children, type = 'button', ...rest }, ref) {
    return (
      <button
        ref={ref}
        type={type}
        aria-label={label}
        title={label}
        className={cx(
          'inline-flex size-9 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-35',
          tone === 'danger' ? 'text-coral hover:bg-coral/10' : 'text-ink-soft hover:bg-mint',
          className,
        )}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

type NoticeTone = 'info' | 'warning' | 'danger';
const noticeStyle: Record<NoticeTone, string> = {
  info: 'bg-mint text-ink ring-brand/20',
  warning: 'bg-amber/15 text-[#6b4700] ring-amber/40',
  danger: 'bg-coral/10 text-[#9b2a26] ring-coral/30',
};
const noticeIcon: Record<NoticeTone, ReactNode> = {
  info: <Info className="mt-0.5 size-4 shrink-0" />,
  warning: <TriangleAlert className="mt-0.5 size-4 shrink-0" />,
  danger: <CircleAlert className="mt-0.5 size-4 shrink-0" />,
};

export function Notice({ tone = 'info', title, children, className }: { tone?: NoticeTone; title?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <div className={cx('flex gap-2.5 rounded-xl px-4 py-3 text-sm ring-1 ring-inset', noticeStyle[tone], className)}>
      {noticeIcon[tone]}
      <div className="min-w-0 space-y-1">
        {title && <div className="font-medium">{title}</div>}
        {children && <div className="leading-relaxed">{children}</div>}
      </div>
    </div>
  );
}

/** Shown in place of a page section when its data failed to load. */
export function LoadError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <div className="mb-3 rounded-full bg-coral/10 p-3">
        <CircleAlert className="size-6 text-coral" />
      </div>
      <div className="font-medium text-ink">未能載入資料</div>
      <p className="mt-1 max-w-sm text-sm text-muted">{errorMessage(error)}</p>
      <Button className="mt-4" variant="secondary" size="sm" icon={<RefreshCw className="size-4" />} onClick={onRetry}>
        再試一次
      </Button>
    </div>
  );
}

export function CopyButton({ text, label = '複製' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  const toast = useToast();
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(false), 1800);
    return () => clearTimeout(t);
  }, [done]);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
    } catch {
      toast.error('未能複製，請手動選取文字複製。');
    }
  }
  return (
    <IconButton label={done ? '已複製' : label} onClick={copy} className="size-8">
      {done ? <Check className="size-4 text-brand-700" /> : <Copy className="size-4" />}
    </IconButton>
  );
}

/** <input type="datetime-local"> that reads and writes ISO strings (null = not set). */
export function DateTimeField({
  label,
  value,
  onChange,
  hint,
  error,
  required,
}: {
  label: ReactNode;
  value: string | null;
  onChange: (iso: string | null) => void;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
}) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {(id) => <Input id={id} type="datetime-local" value={toLocalInput(value)} invalid={!!error} onChange={(e) => onChange(fromLocalInput(e.target.value))} />}
    </Field>
  );
}

/** Image field that uploads to `/api/portal/media` and hands back the stored key + URL. */
export function ImageField({
  label,
  url,
  onChange,
  size,
  hint,
  error,
  aspect,
  required,
  className,
  accept,
  fit,
  frameClassName,
  aiSeed,
}: {
  label: ReactNode;
  url: string | null;
  onChange: (ref: MediaRef | null) => void;
  /** Recommended width × height in pixels, shown as 「建議圖片大小」 before the hint. */
  size?: [number, number];
  hint?: string;
  error?: string;
  aspect?: string;
  required?: boolean;
  className?: string;
  accept?: string;
  fit?: 'contain' | 'cover';
  /** Width of the picture frame, e.g. a small square icon, while the buttons and hint keep the field's width. */
  frameClassName?: string;
  /** What 「AI 生成圖片」 writes the picture's description from (e.g. a 推廣活動's content and tone). */
  aiSeed?: AiImageSeed;
}) {
  const session = useContext(SessionContext);
  const ai = !!session && !session.readOnly && session.hasModule('aiImages');
  return (
    <Field label={label} hint={size ? `建議圖片大小：${size[0]} × ${size[1]} px。${hint ?? ''}` : hint} error={error} required={required} className={className}>
      {() => (
        <>
          <ImageUpload
            url={url}
            aspect={aspect}
            accept={accept}
            fit={fit}
            frameClassName={frameClassName}
            onUpload={async (file) => {
              try {
                onChange(await api.upload<MediaRef>('/media', file));
              } catch (e) {
                throw new Error(errorMessage(e));
              }
            }}
            onClear={() => onChange(null)}
          />
          {ai && <AiImageButton aspect={aspect} purpose={typeof label === 'string' ? label : '圖片'} seed={aiSeed} onUse={onChange} />}
        </>
      )}
    </Field>
  );
}

/** Segmented choice (radio group drawn as pills). */
export function Segmented<K extends string>({ value, options, onChange, label }: { value: K; options: Array<{ value: K; label: string }>; onChange: (v: K) => void; label?: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-full bg-canvas p-1 ring-1 ring-inset ring-line">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cx(
            'rounded-full px-3.5 py-1.5 text-sm transition-colors',
            value === o.value ? 'bg-white font-medium text-ink shadow-sm ring-1 ring-line' : 'text-muted hover:text-ink',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Wraps `LocalizedForm` so its language tab bar can scroll sideways on phones and, when `sticky`,
 * stays under the top bar while a long form scrolls.
 */
export function LangTabsFrame({ children, sticky }: { children: ReactNode; sticky?: boolean }) {
  return (
    <div
      className={cx(
        // Scrolling clips the tab bar's own bottom border, so the divider line is drawn as an inset shadow instead.
        '[&>[role=tablist]]:overflow-x-auto [&>[role=tablist]]:overflow-y-hidden [&>[role=tablist]]:border-b-0 [&>[role=tablist]]:shadow-[inset_0_-1px_0_var(--color-line)]',
        '[&>[role=tablist]>button]:shrink-0 [&>[role=tablist]>button]:whitespace-nowrap',
        'max-sm:[&>[role=tablist]>button]:px-3',
        sticky && '[&>[role=tablist]]:sticky [&>[role=tablist]]:top-16 [&>[role=tablist]]:z-10 [&>[role=tablist]]:bg-white [&>[role=tablist]]:pt-1',
      )}
    >
      {children}
    </div>
  );
}

/** One-line reminder under the language tabs about languages that still have empty required fields. */
export function MissingLangsNote({ missing, defaultLang }: { missing: Lang[]; defaultLang: Lang }) {
  if (missing.length === 0) return null;
  const others = missing.filter((l) => l !== defaultLang);
  const names = missing.map((l) => LANG_META[l].label).join('、');
  return (
    <p className="-mt-2 mb-4 flex items-start gap-1.5 text-xs text-muted">
      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-coral" />
      <span>
        {names}有必填欄位未填寫。
        {missing.includes(defaultLang) ? `預設語言（${LANG_META[defaultLang].label}）必須填寫。` : ''}
        {others.length > 0 ? '未翻譯的語言在會員網站會改為顯示預設語言內容。' : ''}
      </span>
    </p>
  );
}

/**
 * Extra buttons for every SaveBar below the provider (e.g. the website settings put a 「預覽」 button
 * next to 還原／儲存 this way, so each settings tab does not have to pass it).
 */
export const SaveBarExtra = createContext<ReactNode>(null);

/** Save / discard bar that sticks to the bottom of the viewport. In read-only mode it only says so. */
export function SaveBar({ dirty, saving, onSave, onDiscard, saveLabel = '儲存' }: { dirty: boolean; saving: boolean; onSave: () => void; onDiscard?: () => void; saveLabel?: string }) {
  const extra = useContext(SaveBarExtra);
  const readOnly = useContext(SessionContext)?.readOnly ?? false;
  return (
    <div className="sticky bottom-0 z-10 -mx-4 mt-6 border-t border-line bg-white/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <span className={cx('mr-auto text-sm', dirty ? 'text-[#8a5a00]' : 'text-muted')}>
          {readOnly ? '唯讀模式：不能儲存更改' : dirty ? '有未儲存的更改' : '所有更改已儲存'}
        </span>
        {extra}
        {onDiscard && (
          <Button variant="ghost" disabled={!dirty || saving} onClick={onDiscard}>
            還原
          </Button>
        )}
        {!readOnly && (
          <Button loading={saving} disabled={!dirty} onClick={onSave}>
            {saveLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Small number tile (lighter than portal-ui's Stat), for totals above a list. */
export function SummaryTile({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: ReactNode; tone?: 'warning' }) {
  return (
    <div className={cx('rounded-2xl px-4 py-3 ring-1 sm:px-5 sm:py-4', tone === 'warning' ? 'bg-amber/10 ring-amber/40' : 'bg-white ring-line')}>
      <div className="text-sm text-muted">{label}</div>
      <div className="mt-1 text-xl font-medium tracking-tight text-ink tabular-nums sm:text-2xl">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted">{hint}</div>}
    </div>
  );
}

/** Toggle chips for picking several values (weekdays, months, levels, shops…). */
export function ChipGroup<V extends string | number>({
  options,
  value,
  onChange,
  label,
  disabled,
}: {
  options: Array<{ value: V; label: ReactNode; title?: string }>;
  value: readonly V[];
  onChange: (next: V[]) => void;
  label?: string;
  disabled?: boolean;
}) {
  const toggle = (v: V) => onChange(value.includes(v) ? value.filter((x) => x !== v) : options.map((o) => o.value).filter((x) => x === v || value.includes(x)));
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = value.includes(o.value);
        return (
          <button
            key={String(o.value)}
            type="button"
            aria-pressed={on}
            title={o.title}
            disabled={disabled}
            onClick={() => toggle(o.value)}
            className={cx(
              'min-h-9 rounded-full px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              on ? 'bg-ink font-medium text-white' : 'bg-white text-ink-soft ring-1 ring-inset ring-line hover:bg-mint',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Link styled as a secondary button (CSV downloads and page links). */
export const linkButton =
  'inline-flex h-10 items-center justify-center gap-2 rounded-full bg-white px-5 text-[15px] font-medium text-ink ring-1 ring-inset ring-ink/25 transition-colors hover:bg-mint';
export const linkButtonSm =
  'inline-flex h-8 items-center justify-center gap-1.5 rounded-full bg-white px-3.5 text-sm font-medium text-ink ring-1 ring-inset ring-ink/25 transition-colors hover:bg-mint';

/** Asks before leaving a page with unsaved edits (in-app navigation and tab close; a browser shows its own text for the latter). */
export function UnsavedGuard({ when, title = '離開此頁？', message = '你有未儲存的更改，離開後這些更改會遺失。' }: { when: boolean; title?: string; message?: string }) {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => when && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (!when) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [when]);
  return (
    <ConfirmDialog
      open={blocker.state === 'blocked'}
      title={title}
      message={message}
      confirmLabel="離開"
      danger
      onConfirm={() => blocker.proceed?.()}
      onClose={() => blocker.reset?.()}
    />
  );
}
